import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { Plus, Search, ChevronRight, Download, Upload, FileText, AlertTriangle, CheckCircle, X, FileDown, User, Mail, MapPin, Tag } from "lucide-react";

async function downloadExport() {
  const r = await api.get("/customers/export/csv", { responseType: "blob" });
  const url = URL.createObjectURL(new Blob([r.data], { type: "text/csv;charset=utf-8;" }));
  const a = document.createElement("a"); a.href = url; a.download = "kunden_export.csv"; a.click();
  URL.revokeObjectURL(url);
}
async function downloadTemplate() {
  const r = await api.get("/customers/import/template", { responseType: "blob" });
  const url = URL.createObjectURL(new Blob([r.data], { type: "text/csv;charset=utf-8;" }));
  const a = document.createElement("a"); a.href = url; a.download = "kunden_import_vorlage.csv"; a.click();
  URL.revokeObjectURL(url);
}

// ── CSV Import Modal ───────────────────────────────────────────────────
function ImportModal({ onClose, onDone }) {
  const fileRef = useRef();
  const [preview, setPreview] = useState(null);
  const [selected, setSelected] = useState({});
  const [uploading, setUploading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);

  const upload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post("/customers/import/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(r.data);
      // Auto-select non-duplicates
      const sel = {};
      r.data.rows.forEach(row => { sel[row.row_num] = !row.is_duplicate; });
      setSelected(sel);
    } finally { setUploading(false); }
  };

  const toggleAll = (v) => {
    const sel = {};
    preview.rows.forEach(r => { sel[r.row_num] = v; });
    setSelected(sel);
  };

  const doImport = async () => {
    setImporting(true);
    try {
      const rows = preview.rows.filter(r => selected[r.row_num]);
      const r = await api.post("/customers/import/execute", { rows });
      setResult(r.data);
      onDone();
    } finally { setImporting(false); }
  };

  const selectedCount = Object.values(selected).filter(Boolean).length;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="import-modal">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative z-10 bg-card border border-border w-full max-w-3xl max-h-[85vh] flex flex-col shadow-2xl animate-in">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-border flex-shrink-0">
          <div>
            <h2 className="text-lg font-bold">CSV-Import</h2>
            <p className="text-xs text-muted-foreground mt-0.5">Kunden aus CSV-Datei importieren</p>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X size={18} /></button>
        </div>

        <div className="overflow-y-auto flex-1 p-5">
          {!preview && !result && (
            <div className="space-y-4">
              <div className="border-2 border-dashed border-border p-8 text-center">
                <Upload size={32} className="mx-auto text-muted-foreground mb-3" />
                <p className="text-sm font-medium mb-1">CSV-Datei hochladen</p>
                <p className="text-xs text-muted-foreground mb-4">Trennzeichen: Semikolon (;) oder Komma (,)</p>
                <div className="flex items-center justify-center gap-3">
                  <button onClick={() => fileRef.current?.click()} disabled={uploading}
                    className="px-4 py-2 bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 disabled:opacity-50">
                    {uploading ? "Analysiere..." : "Datei wählen"}
                  </button>
                  <button onClick={downloadTemplate} className="flex items-center gap-1.5 px-4 py-2 bg-secondary text-secondary-foreground text-sm font-semibold hover:bg-secondary/80">
                    <FileDown size={14} /> Vorlage herunterladen
                  </button>
                </div>
                <input ref={fileRef} type="file" accept=".csv,.txt" className="hidden" onChange={upload} />
              </div>
              <div className="bg-muted/30 border border-border p-4 text-xs space-y-1">
                <p className="font-bold text-sm mb-2">Pflichtfelder</p>
                <p>• <span className="font-mono font-semibold">Vorname</span> und <span className="font-mono font-semibold">Nachname</span></p>
                <p>• Optionale Spalten: Firma, E-Mail, Telefon, Mobil, Adresse, PLZ, Ort, Land, Webseite, Tags, Notizen</p>
                <p>• Duplikate: Bei gleichem Namen oder E-Mail wird eine Warnung angezeigt</p>
              </div>
            </div>
          )}

          {preview && !result && (
            <div>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <span className="text-sm font-semibold">{preview.total} Zeilen gefunden</span>
                  {preview.duplicates > 0 && (
                    <span className="flex items-center gap-1 text-xs text-orange-600 dark:text-orange-400 bg-orange-50 dark:bg-orange-950/30 px-2 py-0.5 border border-orange-200 dark:border-orange-900">
                      <AlertTriangle size={12} /> {preview.duplicates} mögliche Duplikate
                    </span>
                  )}
                </div>
                <div className="flex gap-2">
                  <button onClick={() => toggleAll(true)} className="text-xs px-2 py-1 bg-secondary hover:bg-secondary/80">Alle</button>
                  <button onClick={() => toggleAll(false)} className="text-xs px-2 py-1 bg-secondary hover:bg-secondary/80">Keine</button>
                </div>
              </div>

              <div className="border border-border overflow-x-auto max-h-64 overflow-y-auto">
                <table className="w-full data-table">
                  <thead>
                    <tr>
                      <th className="w-8"><input type="checkbox" onChange={e => toggleAll(e.target.checked)} className="w-3.5 h-3.5" /></th>
                      <th>Status</th><th>Vorname</th><th>Nachname</th><th>Firma</th><th>E-Mail</th><th>Telefon</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.rows.map(row => (
                      <tr key={row.row_num} className={row.is_duplicate ? "bg-orange-50/50 dark:bg-orange-950/10" : ""}>
                        <td>
                          <input type="checkbox" checked={!!selected[row.row_num]}
                            onChange={e => setSelected(s => ({ ...s, [row.row_num]: e.target.checked }))}
                            className="w-3.5 h-3.5" />
                        </td>
                        <td>
                          {row.is_duplicate ? (
                            <span className="flex items-center gap-1 text-xs text-orange-600 dark:text-orange-400 whitespace-nowrap">
                              <AlertTriangle size={11} /> Duplikat: {row.duplicate_info}
                            </span>
                          ) : (
                            <span className="flex items-center gap-1 text-xs text-green-600 dark:text-green-400">
                              <CheckCircle size={11} /> Neu
                            </span>
                          )}
                        </td>
                        <td className="text-sm">{row.data.first_name}</td>
                        <td className="text-sm">{row.data.last_name}</td>
                        <td className="text-xs text-muted-foreground">{row.data.company || "—"}</td>
                        <td className="text-xs text-muted-foreground">{row.data.email || "—"}</td>
                        <td className="text-xs font-mono">{row.data.phone || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {result && (
            <div className="text-center py-8">
              <CheckCircle size={48} className="mx-auto text-green-500 mb-4" />
              <p className="text-lg font-bold">{result.created} Kunden importiert</p>
              {result.skipped > 0 && <p className="text-sm text-muted-foreground mt-1">{result.skipped} Zeilen übersprungen</p>}
            </div>
          )}
        </div>

        {/* Footer */}
        {preview && !result && (
          <div className="flex items-center justify-between px-5 py-4 border-t border-border flex-shrink-0">
            <p className="text-sm text-muted-foreground"><strong>{selectedCount}</strong> von {preview.total} ausgewählt</p>
            <div className="flex gap-2">
              <button onClick={onClose} className="px-4 py-2 bg-secondary text-sm font-semibold hover:bg-secondary/80">Abbrechen</button>
              <button onClick={doImport} disabled={importing || selectedCount === 0}
                className="px-4 py-2 bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 disabled:opacity-50"
                data-testid="confirm-import-button">
                {importing ? "Importieren..." : `${selectedCount} Kunden importieren`}
              </button>
            </div>
          </div>
        )}
        {result && (
          <div className="px-5 py-4 border-t border-border flex-shrink-0 text-right">
            <button onClick={onClose} className="px-4 py-2 bg-primary text-primary-foreground text-sm font-semibold">Fertig</button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function CustomersPage() {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [form, setForm] = useState({ first_name: "", last_name: "", company: "", email: "", phone: "", mobile: "", website: "", address: "", zip: "", city: "", country: "Schweiz", notes: "", tags: [], status: "active" });
  const [tagInput, setTagInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [dupWarning, setDupWarning] = useState(null); // { duplicate: {...}, pendingForm: {...} }

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/customers", { params: { search, status: statusFilter } });
      setCustomers(r.data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [search, statusFilter]);

  const handleCreate = async (e, force = false) => {
    if (e) e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      if (force) payload.force = true;
      const r = await api.post("/customers", payload);
      if (r.status === 201 || r.data._id) {
        navigate(`/customers/${r.data._id}`);
      }
    } catch (err) {
      if (err.response?.status === 409) {
        setDupWarning({ duplicate: err.response.data.duplicate, pendingForm: { ...form } });
      }
    } finally { setSaving(false); }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Duplicate warning modal */}
      {dupWarning && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60" onClick={() => setDupWarning(null)} />
          <div className="relative z-10 bg-card border border-border p-6 w-full max-w-md shadow-2xl animate-in">
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle size={20} className="text-orange-500" />
              <h3 className="text-base font-bold">Mögliches Duplikat gefunden</h3>
            </div>
            <p className="text-sm text-muted-foreground mb-3">Ein Kunde mit ähnlichem Namen oder E-Mail existiert bereits:</p>
            <div className="bg-orange-50 dark:bg-orange-950/20 border border-orange-200 dark:border-orange-900 p-3 mb-4 text-sm">
              <p className="font-semibold">{dupWarning.duplicate.first_name} {dupWarning.duplicate.last_name}</p>
              <p className="text-muted-foreground text-xs mt-0.5">{dupWarning.duplicate.customer_number}{dupWarning.duplicate.company ? ` · ${dupWarning.duplicate.company}` : ""}{dupWarning.duplicate.email ? ` · ${dupWarning.duplicate.email}` : ""}</p>
            </div>
            <div className="flex gap-3">
              <button onClick={() => { setDupWarning(null); handleCreate(null, true); }}
                className="flex-1 py-2 bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90">
                Trotzdem erstellen
              </button>
              <button onClick={() => setDupWarning(null)}
                className="flex-1 py-2 bg-secondary text-sm font-semibold hover:bg-secondary/80">
                Abbrechen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CSV Import Modal */}
      {showImport && (
        <ImportModal onClose={() => setShowImport(false)} onDone={() => { setShowImport(false); load(); }} />
      )}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Kunden</h1>
          <p className="text-sm text-muted-foreground mt-1">{customers.length} Einträge</p>
        </div>
        <div className="flex gap-2">
          <button data-testid="export-csv-button" onClick={downloadExport}
            className="flex items-center gap-2 px-4 py-2 bg-secondary text-secondary-foreground text-sm font-semibold hover:bg-secondary/80 transition-colors">
            <Download size={16} /> Export
          </button>
          <button data-testid="import-csv-button" onClick={() => setShowImport(true)}
            className="flex items-center gap-2 px-4 py-2 bg-secondary text-secondary-foreground text-sm font-semibold hover:bg-secondary/80 transition-colors">
            <Upload size={16} /> Import
          </button>
          <button data-testid="add-customer-button" onClick={() => setShowForm(!showForm)}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 transition-colors">
            <Plus size={16} /> Neuer Kunde
          </button>
        </div>
      </div>

      {/* Rich add form */}
      {showForm && (
        <form onSubmit={handleCreate} className="bg-card border border-border p-5 mb-5 animate-in space-y-5" data-testid="create-customer-form">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold uppercase tracking-widest text-muted-foreground">Neuer Kunde</h3>
            <button type="button" onClick={() => setShowForm(false)} className="text-muted-foreground hover:text-foreground text-xs">Abbrechen</button>
          </div>

          {/* Persönliche Angaben */}
          <section>
            <h4 className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground/70 mb-2 flex items-center gap-1.5"><User size={11} /> Persönlich</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <FormField label="Vorname *" required value={form.first_name} onChange={v => setForm({ ...form, first_name: v })} testId="customer-form-first_name" />
              <FormField label="Nachname *" required value={form.last_name} onChange={v => setForm({ ...form, last_name: v })} testId="customer-form-last_name" />
              <FormField label="Firma" value={form.company} onChange={v => setForm({ ...form, company: v })} testId="customer-form-company" />
            </div>
          </section>

          {/* Kontakt */}
          <section>
            <h4 className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground/70 mb-2 flex items-center gap-1.5"><Mail size={11} /> Kontakt</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <FormField label="E-Mail" type="email" value={form.email} onChange={v => setForm({ ...form, email: v })} testId="customer-form-email" />
              <FormField label="Website" value={form.website} onChange={v => setForm({ ...form, website: v })} placeholder="www.beispiel.ch" testId="customer-form-website" />
              <FormField label="Telefon" value={form.phone} onChange={v => setForm({ ...form, phone: v })} placeholder="+41 44 123 45 67" testId="customer-form-phone" />
              <FormField label="Mobil" value={form.mobile} onChange={v => setForm({ ...form, mobile: v })} placeholder="+41 79 123 45 67" testId="customer-form-mobile" />
            </div>
          </section>

          {/* Adresse */}
          <section>
            <h4 className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground/70 mb-2 flex items-center gap-1.5"><MapPin size={11} /> Adresse</h4>
            <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
              <div className="md:col-span-6"><FormField label="Strasse / Nr." value={form.address} onChange={v => setForm({ ...form, address: v })} testId="customer-form-address" /></div>
              <div className="md:col-span-2"><FormField label="PLZ" value={form.zip} onChange={v => setForm({ ...form, zip: v })} testId="customer-form-zip" /></div>
              <div className="md:col-span-2"><FormField label="Ort" value={form.city} onChange={v => setForm({ ...form, city: v })} testId="customer-form-city" /></div>
              <div className="md:col-span-2"><FormField label="Land" value={form.country} onChange={v => setForm({ ...form, country: v })} testId="customer-form-country" /></div>
            </div>
          </section>

          {/* Klassifizierung */}
          <section>
            <h4 className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground/70 mb-2 flex items-center gap-1.5"><Tag size={11} /> Klassifizierung</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className="text-xs uppercase tracking-widest font-bold text-muted-foreground block mb-1">Status</label>
                <select data-testid="customer-form-status" value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}
                  className="w-full px-3 py-2 text-sm bg-background border border-input focus:border-primary focus:outline-none">
                  <option value="active">Aktiv</option>
                  <option value="inactive">Inaktiv</option>
                  <option value="prospect">Interessent</option>
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="text-xs uppercase tracking-widest font-bold text-muted-foreground block mb-1">Tags</label>
                <div className="flex flex-wrap gap-1 px-2 py-1.5 min-h-[38px] bg-background border border-input focus-within:border-primary" data-testid="customer-form-tags">
                  {form.tags.map((t, i) => (
                    <span key={i} data-testid={`customer-form-tag-chip-${t}`} className="inline-flex items-center gap-1 text-xs bg-accent text-accent-foreground px-2 py-0.5">
                      {t}
                      <button type="button" onClick={() => setForm({ ...form, tags: form.tags.filter((_, j) => j !== i) })} className="hover:text-destructive">×</button>
                    </span>
                  ))}
                  <input value={tagInput} onChange={e => setTagInput(e.target.value)}
                    onKeyDown={e => {
                      if ((e.key === "Enter" || e.key === ",") && tagInput.trim()) {
                        e.preventDefault();
                        if (!form.tags.includes(tagInput.trim())) setForm({ ...form, tags: [...form.tags, tagInput.trim()] });
                        setTagInput("");
                      } else if (e.key === "Backspace" && !tagInput && form.tags.length) {
                        setForm({ ...form, tags: form.tags.slice(0, -1) });
                      }
                    }}
                    placeholder={form.tags.length ? "" : "Tags mit Enter bestätigen…"}
                    className="flex-1 min-w-[120px] bg-transparent text-sm focus:outline-none"
                    data-testid="customer-form-tag-input" />
                </div>
              </div>
            </div>
          </section>

          {/* Notizen */}
          <section>
            <label className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground/70 block mb-2 flex items-center gap-1.5"><FileText size={11} /> Notizen</label>
            <textarea data-testid="customer-form-notes" rows={3} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })}
              placeholder="Interne Notizen, Besonderheiten, Kontakt-Historie…"
              className="w-full px-3 py-2 text-sm bg-background border border-input focus:border-primary focus:outline-none resize-y" />
          </section>

          <div className="flex gap-2 pt-2 border-t border-border">
            <button data-testid="save-customer-button" type="submit" disabled={saving}
              className="px-5 py-2 bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 disabled:opacity-50">
              {saving ? "Speichern..." : "Kunde anlegen"}
            </button>
            <button type="button" onClick={() => setShowForm(false)}
              className="px-4 py-2 bg-secondary text-secondary-foreground text-sm font-semibold hover:bg-secondary/80">
              Abbrechen
            </button>
          </div>
        </form>
      )}

      {/* Search & Filter */}
      <div className="flex gap-2 mb-4">
        <div className="relative flex-1 max-w-sm">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            data-testid="customer-search-input"
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Suche nach Name, Firma, E-Mail..."
            className="w-full pl-9 pr-3 py-2 text-sm bg-background border border-input focus:border-primary focus:outline-none"
          />
        </div>
        <select
          data-testid="customer-status-filter"
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          className="px-3 py-2 text-sm bg-background border border-input focus:border-primary focus:outline-none"
        >
          <option value="">Alle Status</option>
          <option value="active">Aktiv</option>
          <option value="inactive">Inaktiv</option>
        </select>
      </div>

      {/* Table */}
      <div className="bg-card border border-border" data-testid="customers-table">
        {loading ? (
          <div className="p-8 text-center text-sm text-muted-foreground">Laden...</div>
        ) : customers.length === 0 ? (
          <div className="p-8 text-center text-sm text-muted-foreground">Keine Kunden gefunden</div>
        ) : (
          <table className="w-full data-table">
            <thead>
              <tr>
                <th>Kd-Nr.</th>
                <th>Name</th>
                <th>Firma</th>
                <th>E-Mail</th>
                <th>Telefon</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {customers.map(c => (
                <tr
                  key={c._id}
                  className="cursor-pointer"
                  onClick={() => navigate(`/customers/${c._id}`)}
                  data-testid={`customer-row-${c._id}`}
                >
                  <td className="font-mono text-xs text-muted-foreground">{c.customer_number}</td>
                  <td>
                    <div className="flex items-center gap-2">
                      <div className="initials-avatar text-xs">
                        {(c.first_name?.[0] || "") + (c.last_name?.[0] || "")}
                      </div>
                      <span className="font-medium">{c.first_name} {c.last_name}</span>
                    </div>
                  </td>
                  <td className="text-muted-foreground">{c.company || "—"}</td>
                  <td className="text-muted-foreground">{c.email || "—"}</td>
                  <td className="font-mono text-xs">{c.phone || "—"}</td>
                  <td>
                    <span className={`text-xs font-semibold px-2 py-0.5 ${c.status === "active" ? "status-done" : "status-open"}`}>
                      {c.status === "active" ? "Aktiv" : "Inaktiv"}
                    </span>
                  </td>
                  <td><ChevronRight size={14} className="text-muted-foreground" /></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function FormField({ label, type = "text", required, value, onChange, placeholder, testId }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-widest font-bold text-muted-foreground block mb-1">{label}</label>
      <input
        data-testid={testId}
        type={type}
        required={required}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full px-3 py-2 text-sm bg-background border border-input focus:border-primary focus:outline-none"
      />
    </div>
  );
}

